import { useState, useEffect, ChangeEvent, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
    Box,
    HStack,
    Input,
    Radio,
    RadioGroup,
    Select,
    Grid,
    GridItem,
    Text,
    useDisclosure,
    AlertDialog,
    AlertDialogOverlay,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogCloseButton,
    AlertDialogBody,
    AlertDialogFooter,
    useToast,
    Button,
    Spinner,
    Center,
} from '@chakra-ui/react';

import PageHeader from 'layout/pageHeader';
import {
    IList,
    useGetListDataQuery,
    useUpdateListDataMutation,
    useAddListDataMutation,
    useGetListsQuery,
    useGetListTypeQuery,
} from 'core/API/listApi';
import { useGetCompanyInfoSelector, useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';
import {
    useRetailers,
    useKeywords,
    useCategories,
    useListKey,
    useSegments,
    useCats,
} from 'core/redux/selectors/dataSelectors';
import { DEFAULT_LIST } from 'pages/ListManagement/const';
import { checkRequestStatus } from 'utils/erros';
import { LocaleKey, useTranslation } from 'helpers/languages/translations';
import { fetchListData, KeyOflistType, listTypes } from '../ListTemplates/helpers/HeadersList';
import DescriptionHtml from 'components/molecules/DescriptionHtml';

const MListDataViewer = () => {
    const navigate = useNavigate();
    const translate = useTranslation();
    const toast = useToast();
    const goBack = () => navigate(-1);
    const location = useLocation();
    const companyInfo = useGetCompanyInfoSelector();
    const [listDataUpload, setListDataUpload] = useState<IList>();
    const [listData, setListData] = useState<IList>();
    const [listName, setListName] = useState<string>('');
    const [uniqType, setUniqType] = useState<string[]>();
    const [userListTypes, setUserListType] = useState<string[]>();

    const listId = location.state.listId as number;
    const toolId = location.state.toolId as number;

    const { isOpen, onOpen, onClose } = useDisclosure();

    const userInfo = useGetUserInfoSelector();
    const readonly = !userInfo?.user_groups?.includes(22);

    const [updateListData, { isLoading: isUpdateListLoading }] = useUpdateListDataMutation();
    const [addListData, { isLoading: isAddListLoading }] = useAddListDataMutation();

    const [isAdd, _] = useState<boolean>(!listId);
    const { data: listsArray, isError: isErrorArray, isLoading: isLoadingArray } = useGetListsQuery({ idTool: toolId });
    const {
        data: listTypeArray,
        isError: isTypeErrorArray,
        isLoading: isTypeLoadingArray,
    } = useGetListTypeQuery({ idTool: toolId });

    const { data: lists, isError, isLoading } = useGetListDataQuery({ idTool: toolId, idList: listId });

    const { retailers, isLoading: isRetailerLoading } = useRetailers();
    const { keywords, isLoading: isKeywordsLoading } = useKeywords();
    const { categories, isLoading: isCategoriesLoading } = useCategories();
    const { segments, isLoading: isSegmentsLoading } = useSegments();
    const { cats, isLoading: isCatsLoading } = useCats();
    const { list, isLoading: isStatusLoading } = useListKey(companyInfo?.id || 0);

    useEffect(() => {
        if (location.state.listId && !isError && !isLoading && lists?.lists) {
            setListData(lists.lists[0]);
            setListName(lists.lists[0].name);
        }
    }, [isError, isLoading, lists]);

    useEffect(() => {
        if (!isTypeErrorArray && !isTypeLoadingArray) {
            setUserListType(listTypeArray?.lists.map((userType) => userType.name));
        }
    }, [isTypeLoadingArray, isTypeErrorArray, listTypeArray]);

    const listTypeTranslated: { type: string; name: string }[] = listTypes[toolId as KeyOflistType]
        .map((type) => {
            return { type, name: translate(`list.type.${type}` as LocaleKey) as string };
        })
        .filter((type) => userListTypes?.includes(type.type))
        .sort((a, b) => a.name.localeCompare(b.name));

    useEffect(() => {
        if (!isErrorArray && !isLoadingArray) {
            const typeSet = new Set<string>();
            listsArray?.lists.forEach((listDataMap) => {
                if (listDataMap.type_unique) {
                    typeSet.add(listDataMap.type);
                }
            });
            setUniqType(Array.from(typeSet));
        }
    }, [isErrorArray, isLoadingArray, listsArray]);

    const isTypeAuthorized = (type: string) => {
        return uniqType?.findIndex((typeUniq) => type === typeUniq) !== -1;
    };

    const onChangeListName = (e: ChangeEvent<HTMLInputElement>) => {
        if (listData) {
            const newList = { ...listData };
            newList.name = e.target.value;
            setListData(newList);
            setListName(e.target.value);
        }
    };

    const handleChangeListType = (event: ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        const listDataFetched = fetchListData(
            toolId,
            value,
            DEFAULT_LIST,
            companyInfo,
            retailers || [],
            keywords || [],
            categories || [],
            segments || [],
            list || [],
            cats || [],
        );

        const newList: IList = {
            ...DEFAULT_LIST,
            type: value,
            columns: listDataFetched.headers,
            data: [[...listDataFetched.headers.map(() => '')]],
        };
        setListName('');
        setListData(newList);
    };

    const handleUploadListData = async () => {
        try {
            if (listDataUpload) {
                const res = isAdd
                    ? addListData({
                          idTool: toolId,
                          lists: { ...listDataUpload, upc_type: companyInfo ? companyInfo?.upc_type : '0' },
                      })
                    : await updateListData({ idTool: toolId, list: listDataUpload });

                checkRequestStatus(res, translate(`list.edit.error`) as string);

                toast({
                    title: translate(`list.edit.success`),
                    status: 'success',
                    isClosable: true,
                });
            }
        } catch {
            toast({
                title: translate(`list.edit.error`),
                status: 'error',
                isClosable: true,
            });
        }
        onClose();
    };

    const updateTable = () => {
        const cancelRef = useRef(null);
        return (
            <>
                <AlertDialog
                    motionPreset="slideInBottom"
                    leastDestructiveRef={cancelRef}
                    onClose={onClose}
                    isOpen={isOpen}
                    isCentered
                >
                    <AlertDialogOverlay />

                    <AlertDialogContent>
                        <AlertDialogHeader>{translate(`list.modal.upload.title`)}</AlertDialogHeader>
                        <AlertDialogCloseButton />
                        <AlertDialogBody>{translate(`list.modal.upload.message`)}</AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onClose}>
                                {translate(`no`)}
                            </Button>
                            <Button
                                isLoading={isUpdateListLoading}
                                onClick={handleUploadListData}
                                colorScheme="red"
                                ml={3}
                            >
                                {translate(`yes`)}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </>
        );
    };

    const setListStatus = (status: string) => {
        if (listData) {
            const newListData: IList = { ...listData };
            newListData.is_active = status === 'Active';
            setListData(newListData);
        }
    };

    return (
        <Box>
            {updateTable()}
            <PageHeader
                icon={{ name: 'arrowBack', props: { onClick: goBack } }}
                pageTitle="Lists / View my List"
                readonly={readonly}
            />
            <Box px="32px">
                {isLoading ? (
                    <Center w="100%" h="200px" bg="white" border="1px solid" borderColor="gray.100">
                        <Spinner />
                    </Center>
                ) : (
                    <Grid templateColumns="repeat(5, 1fr)" gap={4}>
                        <GridItem colSpan={1}>
                            <Box m={2}>
                                <Text as="b" fontSize="sm">
                                    {translate('list.title.type')}
                                </Text>
                                <Select
                                    onChange={handleChangeListType}
                                    disabled={location.state.listId !== 0 || readonly}
                                    placeholder={translate('list.placeholder.type') as string}
                                    value={listData?.type}
                                >
                                    {listTypeTranslated.map((type) => (
                                        <option value={type.type} disabled={!listId && isTypeAuthorized(type.type)}>
                                            {type.name}
                                        </option>
                                    ))}
                                </Select>
                                {isAdd && <Text fontSize="xs">{translate(`list.add.warning` as LocaleKey)}</Text>}
                            </Box>
                            <Box m={2}>
                                <Text as="b" fontSize="sm">
                                    {translate('list.title.name')}
                                </Text>
                                <Input onChange={onChangeListName} value={listName} size="sm" isDisabled={readonly} />
                            </Box>
                            <Box m={2}>
                                <Text as="b" fontSize="sm">
                                    {translate('list.title.status')}
                                </Text>
                                <RadioGroup
                                    onChange={setListStatus}
                                    value={listData?.is_active ? 'Active' : 'Inactive'}
                                    isDisabled={readonly}
                                >
                                    <HStack spacing="24px">
                                        <Radio value="Active">{translate('list.title.active')}</Radio>
                                        <Radio value="Inactive">{translate('list.title.inactive')}</Radio>
                                    </HStack>
                                </RadioGroup>
                                {listData && <DescriptionHtml tradKey={`list.${listData.type}.desc`} mt={3} />}
                            </Box>
                        </GridItem>
                        <GridItem colSpan={4}>
                            {listData &&
                                !isRetailerLoading &&
                                !isKeywordsLoading &&
                                !isCategoriesLoading &&
                                !isTypeLoadingArray &&
                                fetchListData(
                                    toolId,
                                    listData.type,
                                    listData,
                                    companyInfo,
                                    retailers || [],
                                    keywords || [],
                                    categories || [],
                                    segments || [],
                                    list || [],
                                    cats || [],
                                ).widget}
                        </GridItem>
                    </Grid>
                )}
            </Box>
        </Box>
    );
};

export default MListDataViewer;
